import { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { Loader2, Download, FileSpreadsheet, PoundSterling, Receipt, Wallet, Info, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Provider Accounting tab. Tax-time summary of bookings paid through
 * Zuriya, with CSV export ready for an accountant or HMRC Self Assessment.
 *
 * Defaults to the current UK tax year (6 Apr to 5 Apr) since that's the
 * most useful filing-period view for sole traders.
 */

function currentUkTaxYearOptions() {
  const now = new Date();
  // UK tax year starts 6 Apr. If we're before 6 Apr, current year started last calendar year.
  const cutoff = new Date(now.getFullYear(), 3, 6); // Apr is month 3 (0-indexed)
  const startYear = now < cutoff ? now.getFullYear() - 1 : now.getFullYear();
  const options = [];
  for (let i = 0; i < 4; i++) {
    const y = startYear - i;
    const y2 = String((y + 1) % 100).padStart(2, '0');
    options.push({ value: `tax:${y}-${y2}`, label: `UK tax year ${y}/${y2}` });
  }
  // Calendar years too
  for (let i = 0; i < 3; i++) {
    const y = now.getFullYear() - i;
    options.push({ value: `calendar:${y}`, label: `Calendar year ${y}` });
  }
  options.push({ value: 'all', label: 'All time' });
  return options;
}

function fmtGbp(n) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
}

function monthLabel(ym) {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split('-');
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

export function AccountingTab({ provider }) {
  const yearOptions = currentUkTaxYearOptions();
  const [year, setYear] = useState(yearOptions[0].value);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/providers/me/accounting/summary?year=${encodeURIComponent(year)}`);
      setSummary(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load accounting summary');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/providers/me/accounting/transactions.csv?year=${encodeURIComponent(year)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      const safeLabel = (summary?.label || year).toLowerCase().replace(/\W+/g, '-');
      link.setAttribute('download', `zuriya-accounting-${safeLabel}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const totals = summary?.totals;

  return (
    <div className="space-y-6" data-testid="accounting-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl font-medium text-[#1A1A1A]">Accounting</h2>
          <p className="text-sm text-stone-500 mt-1 max-w-2xl">
            Tax-time summary of bookings paid through Zuriya. Download as
            CSV to hand to your accountant or use directly when filing
            HMRC Self Assessment.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-56" data-testid="accounting-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={handleDownload}
            disabled={downloading || loading || (totals?.booking_count || 0) === 0}
            className="btn-primary"
            data-testid="accounting-download-csv-btn"
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download CSV
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-lg" />)}
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              icon={<PoundSterling className="h-5 w-5" />}
              label="Gross revenue"
              value={fmtGbp(totals?.gross_revenue)}
              caption={`${totals?.booking_count || 0} bookings`}
            />
            <SummaryCard
              icon={<Receipt className="h-5 w-5" />}
              label="Platform commission"
              value={fmtGbp(totals?.platform_commission)}
              caption={`Tier: ${(summary?.provider?.subscription_tier || 'free') === 'free' ? '15%' : '10%'} typical`}
              tone="muted"
            />
            <SummaryCard
              icon={<TrendingDown className="h-5 w-5" />}
              label="Refunds"
              value={fmtGbp(totals?.refund_amount)}
              caption={`Processing fee: ${fmtGbp(totals?.processing_fee)}`}
              tone="muted"
            />
            <SummaryCard
              icon={<Wallet className="h-5 w-5" />}
              label="Provider net"
              value={fmtGbp(totals?.provider_net)}
              caption="What landed in your payouts"
              tone="primary"
            />
          </div>

          {/* By month */}
          <div className="card-surface p-6">
            <h3 className="font-heading text-lg font-medium text-[#1A1A1A] mb-4">
              {summary?.label} breakdown
            </h3>
            {(summary?.by_month || []).length === 0 ? (
              <div className="py-12 text-center text-stone-500">
                <FileSpreadsheet className="h-10 w-10 mx-auto mb-3 text-stone-300" />
                <p className="text-sm">No bookings in this period yet.</p>
                <p className="text-xs text-stone-400 mt-1">Try another year, or pick "All time".</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-stone-500 border-b border-stone-100">
                      <th className="py-2 pr-4 font-medium">Month</th>
                      <th className="py-2 pr-4 font-medium text-right">Bookings</th>
                      <th className="py-2 pr-4 font-medium text-right">Gross revenue</th>
                      <th className="py-2 font-medium text-right">Provider net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.by_month.map(m => (
                      <tr key={m.month} className="border-b border-stone-50">
                        <td className="py-3 pr-4 text-[#1A1A1A]">{monthLabel(m.month)}</td>
                        <td className="py-3 pr-4 text-right text-stone-600">{m.booking_count}</td>
                        <td className="py-3 pr-4 text-right text-stone-700">{fmtGbp(m.gross_revenue)}</td>
                        <td className="py-3 text-right font-medium text-[#1A1A1A]">{fmtGbp(m.provider_net)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-stone-200">
                      <td className="py-3 pr-4 font-semibold text-[#1A1A1A]">Total</td>
                      <td className="py-3 pr-4 text-right font-semibold">{totals?.booking_count}</td>
                      <td className="py-3 pr-4 text-right font-semibold">{fmtGbp(totals?.gross_revenue)}</td>
                      <td className="py-3 text-right font-semibold text-[#D4AF37]">{fmtGbp(totals?.provider_net)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* HMRC Self Assessment note */}
          <div className="rounded-lg border border-stone-200 bg-stone-50 px-4 py-3 flex items-start gap-3">
            <Info className="h-4 w-4 text-stone-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-stone-600 leading-relaxed">
              <strong className="text-stone-700">For HMRC Self Assessment:</strong>{' '}
              Your "Provider net" total is the income figure to enter on your SA103 self-employment
              pages. Platform commission and processing fees are already deducted. If you have other
              business expenses (products, travel, insurance), add them in your accountant's tally.
              {summary?.notice && (
                <div className="mt-2 text-stone-500">{summary.notice}</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, caption, tone = 'default' }) {
  const tones = {
    default: 'bg-white border-stone-200',
    muted: 'bg-stone-50 border-stone-100',
    primary: 'bg-[#1A1A1A] text-white border-[#1A1A1A]',
  };
  const labelClass = tone === 'primary' ? 'text-white/70' : 'text-stone-500';
  const captionClass = tone === 'primary' ? 'text-white/60' : 'text-stone-400';
  const iconClass = tone === 'primary' ? 'text-[#D4AF37]' : 'text-stone-400';

  return (
    <div className={`rounded-lg border p-4 ${tones[tone]}`}>
      <div className={`flex items-center gap-2 mb-2 ${iconClass}`}>
        {icon}
        <span className={`text-xs uppercase tracking-wide font-medium ${labelClass}`}>{label}</span>
      </div>
      <div className="text-2xl font-semibold">{value}</div>
      {caption && <div className={`text-xs mt-1 ${captionClass}`}>{caption}</div>}
    </div>
  );
}
